// F-097 Marque et support professionnels.
import { useEffect, useState } from "react";
import { del, put, upload } from "@core/api";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { Button, Card, ErrorBox, FileDrop, Input, Loading, Notice, PageHeader, Textarea, fieldError } from "@core/ui";
import { useRun } from "./shared";

export function BrandingPage() {
  const org = useOrg();
  const q = useGet<any>(org.api("/pro/branding"));
  const invalidate = useInvalidate();
  const { busy, error, run } = useRun();
  const logo = useRun();
  const [form, setForm] = useState({ display_name: "", accent_hex: "#2B2F8F", support_email: "", support_phone: "", support_hours: "", support_url: "", legal_notice: "" });
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const b = q.data?.branding;
    if (b) {
      setForm({
        display_name: b.display_name ?? "",
        accent_hex: b.accent_hex ?? "#2B2F8F",
        support_email: b.support_email ?? "",
        support_phone: b.support_phone ?? "",
        support_hours: b.support_hours ?? "",
        support_url: b.support_url ?? "",
        legal_notice: b.legal_notice ?? "",
      });
    }
  }, [q.data]);
  if (q.isLoading) return <Loading />;
  const editable = org.can("members");
  const presets: any[] = q.data?.presets ?? [];
  const hasLogo = !!q.data?.branding?.logo_document_id;
  return (
    <div className="page">
      <PageHeader
        eyebrow="Professionnels"
        title="Marque et support"
        intro="Votre nom, votre logo et une couleur d’accent apparaissent sur le portail des mandants et sur les pages de validation de dépenses. Les mentions de Bailivo et les responsabilités de chacun restent affichées."
      />
      <Notice tone="attention" title="Engagement de service">
        Indiquez des horaires et des moyens de contact, pas un délai de réponse ferme : un niveau de service ne s’affiche qu’une fois contractualisé avec vos clients.
      </Notice>
      <Card title="Aperçu">
        <div className="pro-brand" style={{ background: form.accent_hex, color: "#FFFFFF" }}>
          {hasLogo ? <img src={`/api/v1/public/orgs/${org.orgId}/branding/logo?v=${version}`} alt={`Logo de ${form.display_name || org.orgName}`} /> : null}
          <strong>{form.display_name || org.orgName}</strong>
        </div>
        <p className="small muted">Service fourni par Bailivo. Texte blanc sur la couleur choisie : contraste d’au moins 4,5:1 vérifié par le serveur.</p>
      </Card>
      <Card title="Identité">
        <form
          className="stack"
          onSubmit={async (e) => {
            e.preventDefault();
            if (await run(() => put(org.api("/pro/branding"), form), "Marque enregistrée.")) invalidate(org.api("/pro/branding"));
          }}
        >
          <Input label="Nom affiché" value={form.display_name} disabled={!editable} error={fieldError(error, "display_name")} placeholder={org.orgName} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
          <fieldset>
            <legend>Couleur d’accent</legend>
            <div className="pro-swatches">
              {presets.map((p) => (
                <button key={p.hex} type="button" className="pro-swatch" aria-pressed={form.accent_hex.toUpperCase() === p.hex} disabled={!editable} onClick={() => setForm({ ...form, accent_hex: p.hex })}>
                  <span className="pro-swatch-dot" style={{ background: p.hex }} aria-hidden="true" />
                  {p.label}
                  <span className="small muted">{String(p.contrast_white).replace(".", ",")}:1</span>
                </button>
              ))}
            </div>
            <Input
              label="Ou une couleur personnalisée (#RRGGBB)"
              value={form.accent_hex}
              disabled={!editable}
              error={fieldError(error, "accent_hex")}
              help="Refusée si le texte blanc n’y est pas lisible (contraste inférieur à 4,5:1)."
              onChange={(e) => setForm({ ...form, accent_hex: e.target.value })}
            />
          </fieldset>
          <h3>Support</h3>
          <div className="form-row">
            <Input label="E-mail de support" type="email" value={form.support_email} disabled={!editable} error={fieldError(error, "support_email")} onChange={(e) => setForm({ ...form, support_email: e.target.value })} />
            <Input label="Téléphone de support" type="tel" value={form.support_phone} disabled={!editable} onChange={(e) => setForm({ ...form, support_phone: e.target.value })} />
          </div>
          <div className="form-row">
            <Input label="Horaires" value={form.support_hours} disabled={!editable} error={fieldError(error, "support_hours")} placeholder="Du lundi au vendredi, 9 h – 17 h" onChange={(e) => setForm({ ...form, support_hours: e.target.value })} />
            <Input label="Page d’aide (https://…)" value={form.support_url} disabled={!editable} error={fieldError(error, "support_url")} onChange={(e) => setForm({ ...form, support_url: e.target.value })} />
          </div>
          <Textarea label="Mentions de votre société" value={form.legal_notice} disabled={!editable} error={fieldError(error, "legal_notice")} help="Carte professionnelle, garantie financière, médiateur… Elles s’ajoutent aux mentions de Bailivo sans les remplacer." onChange={(e) => setForm({ ...form, legal_notice: e.target.value })} />
          <ErrorBox error={error && !(error as any).fields?.length ? error : null} />
          {editable ? (
            <div>
              <Button type="submit" busy={busy}>
                Enregistrer la marque
              </Button>
            </div>
          ) : null}
        </form>
      </Card>
      {editable ? (
        <Card title="Logo">
          <FileDrop
            label="Déposer le logo"
            accept="image/png,image/jpeg,image/webp"
            help="PNG, JPEG ou WebP, 1 Mo au plus. Le SVG n’est pas accepté."
            busy={logo.busy}
            onFiles={async (files) => {
              const f = new FormData();
              f.append("file", files[0]);
              if (await logo.run(() => upload(org.api("/pro/branding/logo"), f), "Logo enregistré.")) {
                setVersion(version + 1);
                invalidate(org.api("/pro/branding"));
              }
            }}
          />
          {hasLogo ? (
            <Button
              variant="discret"
              small
              onClick={async () => {
                if (await logo.run(() => del(org.api("/pro/branding/logo")), "Logo retiré.")) invalidate(org.api("/pro/branding"));
              }}
            >
              Retirer le logo
            </Button>
          ) : null}
          <ErrorBox error={logo.error} />
        </Card>
      ) : null}
    </div>
  );
}
